import 'fullscreen-api-polyfill'

import type {TimeSync} from "timesync";
import * as timesync from "timesync";
import pws from "pws";
import {Ref, RefObject, useCallback, useEffect, useRef, useState} from "react";
import {MatchTiming} from "./javaTypes";
import { Temporal } from "temporal-polyfill";

// To allow implicit casting of non-string args
export const PermissiveURLSearchParams = (init: Record<string, string | number | boolean | number[]>) => new URLSearchParams(init as Record<string, string>)

declare global {
    interface Document {
        webkitFullscreenElement: Element | null;
        webkitExitFullscreen: () => Promise<void>;
    }
    interface Element {
        webkitRequestFullscreen: (options?: FullscreenOptions) => Promise<void>;
    }
}

export function toggleFullscreen() {
    if (isFullScreen()) {
        if(document.exitFullscreen) {
            return document.exitFullscreen();
        } else {
            return document.webkitExitFullscreen();
        }
    } else {
        if(document.documentElement.requestFullscreen) {
            return document.documentElement.requestFullscreen();
        } else {
            return document.documentElement.webkitRequestFullscreen();
        }
    }
}

export function fullscreenElement() : Element | null {
    return document.fullscreenElement ?? document.webkitFullscreenElement;
}

export function isFullScreen() : boolean {
    return !!fullscreenElement();
}

export function enableFullscreen() {
    if (!isFullScreen()) {
        return toggleFullscreen();
    }
    return Promise.resolve();
}

export function disableFullscreen() {
    if (isFullScreen()) {
        return toggleFullscreen();
    }
    return Promise.resolve();
}

export function useFullscreenState(ref?: RefObject<Element>) {
    const fullscreenChecker = useCallback(() => {
        const element = fullscreenElement();
        if(ref) {
            return !!element && element == ref.current
        } else {
            return !!element
        }
    }, [ref]);
    const [fullscreen, setFullscreen] = useState(fullscreenChecker);

    useEffect(() => {
        const fullscreenListener = () => setFullscreen(fullscreenChecker)
        document.documentElement.addEventListener("fullscreenchange", fullscreenListener);
        document.documentElement.addEventListener("webkitfullscreenchange", fullscreenListener);
        return () => {
            document.documentElement.removeEventListener("fullscreenchange", fullscreenListener);
            document.documentElement.removeEventListener("webkitfullscreenchange", fullscreenListener);
        }
    }, []);

    return fullscreen
}

export function usePersistentWebsocket<In>(url: string | null, messageHandler: (data: In) => void, onOpen?: (socket: WebSocket) => void) {
    const [isConnected, setConnected] = useState<boolean>();
    const socketRef = useRef<WebSocket>();
    // If after a second we never connected, mark us as disconnected
    useEffect(() => {
        setTimeout(() => {
            setConnected((was) => was === undefined ? false : was)
        }, 1000)
    }, []);
    useEffect(() => {
        if(!url) {
            return;
        }
        let urlWithProtocol = url;
        if(urlWithProtocol.startsWith("/")) {
            urlWithProtocol = `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}${url}`;
        }
        const newSocket = pws(urlWithProtocol, {
            // Server will send every 10
            pingTimeout: 15 * 1000
        });
        socketRef.current = newSocket;

        newSocket.onmessage = function(msg){
            if(msg.data === 'pong') {
                return;
            }
            if(msg.data.startsWith("APP_VERSION:")) {
                const version = msg.data.substring("APP_VERSION:".length);
                if(window.FIRST_ENV && version !== window.FIRST_ENV.appVersion) {
                    console.log(`App version mismatch, reloading (frontend ${window.FIRST_ENV.appVersion}, server ${version})`);
                    window.location.reload();
                }
                return;
            }
            let data = JSON.parse(msg.data);
            messageHandler(data);
        }
        newSocket.onopen = () => {
            newSocket.send("APP_VERSION")
            setConnected(true);
            if(onOpen) {
                onOpen(newSocket);
            }
        }
        newSocket.onclose = (evt) => {
            setConnected(false);
        }

        return () => {
            if (socketRef.current) {
                // Clear the onclose handler because we may be concurrently opening a new socket, so ensure we avoid race
                // conditions on the disconnect banner
                setConnected(false)
                socketRef.current.onclose = null;
                socketRef.current.close();
                socketRef.current = undefined;
            }
        }
    }, [url]);
    return {
        // If we haven't connected yet, pretend we are to avoid flashes of UI
        isConnected: isConnected === undefined ? true : isConnected,
        send: (payload: string) => socketRef.current && isConnected ? socketRef.current.send(payload) : console.warn("Socket disconnected, could not send message"),
    }
}

export function usePersistentWebsocketWithTime<In>(url: string | null, messageHandler: (data: In) => void, onOpen?: (socket: PersistentWebsocketWithTime) => void) {
    const [isConnected, setConnected] = useState<boolean>();
    const socketRef = useRef<PersistentWebsocketWithTime>();
    const callbacksRef = useRef({ messageHandler, onOpen })
    // If the handler changes, we want to ensure the new handler is immediately called
    callbacksRef.current.messageHandler = messageHandler;
    callbacksRef.current.onOpen = onOpen;
    // If after a second we never connected, mark us as disconnected
    useEffect(() => {
        setTimeout(() => {
            setConnected((was) => was === undefined ? false : was)
        }, 1000)
    }, []);
    useEffect(() => {
        if(!url) {
            return;
        }
        const newSocket = new PersistentWebsocketWithTime(url);
        socketRef.current = newSocket;
        newSocket.handleMessage = function(evt){
            let data = JSON.parse(evt.data);
            callbacksRef.current.messageHandler(data);
        }
        newSocket.onopen = () => {
            setConnected(true);
            if(callbacksRef.current.onOpen) {
                callbacksRef.current.onOpen(newSocket);
            }
        }
        newSocket.onclose = (evt) => {
            setConnected(false);
        }

        return () => {
            if (socketRef.current) {
                // Clear the onclose handler because we may be concurrently opening a new socket, so ensure we avoid race
                // conditions on the disconnect banner
                setConnected(false)
                socketRef.current.onclose = null;
                socketRef.current.close();
                socketRef.current = undefined;
            }
        }
    }, [url]);

    return {
        // If we haven't connected yet, pretend we are to avoid flashes of UI
        isConnected: isConnected === undefined ? true : isConnected,
        send: (payload: string) => socketRef.current ? socketRef.current.send(payload) : console.warn("Socket disconnected, could not send message"),
        now: () => socketRef.current ? socketRef.current.now() : Date.now()
    }
}

export class PersistentWebsocketWithTime {
    socket: WebSocket;
    ts: TimeSync<WebSocket>;
    onopen?: ((this: PersistentWebsocketWithTime, ev: Event) => any) | null;
    onclose?: ((this: PersistentWebsocketWithTime, ev: CloseEvent) => any) | null;
    handleMessage?: ((this: PersistentWebsocketWithTime, ev: MessageEvent) => any) | null;

    constructor(url: string) {
        if(url.startsWith("/")) {
            url = `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}${url}`;
        }
        this.socket = pws(url, {
            // Server will send every 10
            pingTimeout: 15 * 1000
        })
        this.ts = timesync.create({
            server: this.socket,
            interval: 30000
        });

        this.ts.send = async function (socket, data, timeout) {
            // TODO do we need to use the timeout
            return socket.send("TIMESYNC:" + JSON.stringify(data));
        };

        this.socket.onmessage = (msg) => {
            if(msg.data === 'pong') {
                return;
            }
            if(msg.data.startsWith("TIMESYNC:")) {
                this.ts.receive(null, JSON.parse(msg.data.substring("TIMESYNC:".length)));
                return;
            }
            if(msg.data.startsWith("APP_VERSION:")) {
                const version = msg.data.substring("APP_VERSION:".length);
                if(window.FIRST_ENV && version !== window.FIRST_ENV.appVersion) {
                    console.log(`App version mismatch, reloading (frontend ${window.FIRST_ENV.appVersion}, server ${version})`);
                    window.location.reload();
                }
                return;
            }
            if(this.handleMessage) {
                this.handleMessage(msg);
            }
        }

        this.socket.onopen = (evt) => {
            this.socket.send("APP_VERSION")
            if(this.onopen) {
                this.onopen(evt)
            }
        }

        this.socket.onclose = (evt) => {
            if(this.onclose) {
                this.onclose(evt)
            }
        }
    }

    now() {
        return this.ts.now();
    }

    close() {
        this.ts.destroy();
        this.socket.close();
    }

    send(data : string | BufferSource | Blob) {
        this.socket.send(data);
    }
}

let uniqueId = 0;
const getUniqueId = () => uniqueId++;

export function useUniqueId() {
    const idRef = useRef(`uniq_${getUniqueId()}`);
    return idRef.current;
}

export function renderTime(s : number, withHours?: boolean) {
    const neg = s < 0;
    if (neg) {
        s = -s;
    }
    let str = neg ? "+" : "";
    // Hours should only be rendered if they exist, whereas minutes should always be rendered
    if(s > 3600 && withHours) {
        return str + String(Math.floor(s / 3600)) + ":" + String(Math.floor(s % 3600 / 60)).padStart(2, "0") + ":" + String(Math.floor(s % 60)).padStart(2, "0");
    }
    return str + Math.floor(s / 60) + ":" + String(Math.floor(s % 60)).padStart(2, "0");
}

export function normalizeUrl(url: string) {
    if(window.FIRST_ENV.projectName == 'FTCCloud' && url.endsWith("/")) {
        return url.substring(0, url.length - 1);
    }
    if(window.FIRST_ENV.projectName == 'FTCLocal' && !url.endsWith("/")) {
        return url + "/";
    }
    return url;
}

export function seasonHasRandom(cmpYear: number) {
    return cmpYear !== 2025;
}

export interface ParsedMatchTiming {
    auto: Temporal.Duration;
    transition: Temporal.Duration;
    teleop: Temporal.Duration;
    endgameWarning: Temporal.Duration;
    total: Temporal.Duration;
}

export function parseMatchTiming(timing: MatchTiming): ParsedMatchTiming {
    return {
        auto: Temporal.Duration.from(timing.auto),
        transition: Temporal.Duration.from(timing.transition),
        teleop: Temporal.Duration.from(timing.teleop),
        endgameWarning: Temporal.Duration.from(timing.endgameWarning),
        total: Temporal.Duration.from(timing.total)
    }
}